import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const EMPTY = { title: "", date: "", link: "", note: "", dateTbd: false };

export default function SubmitEventDialog({ open, onOpenChange }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (f.title.trim().length < 4) return toast.error("Give it a short, clear title");
    if (!/^https?:\/\//.test(f.link.trim())) return toast.error("A source link (https://…) is required — an X post works");
    if (!f.date && !f.dateTbd) return toast.error("Pick a date or tick 'date not announced yet'");
    setSaving(true);
    try {
      const { data } = await api.post("/catalysts/submit", { ...f, title: f.title.trim(), link: f.link.trim() });
      toast.success(data.message);
      setF(EMPTY); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[#334566] max-w-md" data-testid="submit-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Submit an event</DialogTitle>
          <DialogDescription className="text-slate-400">Saw a catalyst announced on X? Drop it here with the link. An admin reviews before it goes live.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase">Title</label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Tesla Roadster showcase" className="bg-[#111723] border-[#334566]" data-testid="submit-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase">Date</label>
              <Input type="date" value={f.date} disabled={f.dateTbd} onChange={(e) => set("date", e.target.value)} className="bg-[#111723] border-[#334566]" data-testid="submit-date-input" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 pb-2.5 cursor-pointer">
              <Checkbox checked={f.dateTbd} onCheckedChange={(v) => set("dateTbd", Boolean(v))} data-testid="submit-tbd-checkbox" /> Date not announced yet
            </label>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase">Link (required)</label>
            <Input value={f.link} onChange={(e) => set("link", e.target.value)} placeholder="https://x.com/…" className="bg-[#111723] border-[#334566]" data-testid="submit-link-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-mono uppercase">Note (optional)</label>
            <Textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} placeholder="Why it matters / which coins" className="bg-[#111723] border-[#334566]" data-testid="submit-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-[#334566] bg-transparent" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="submit-event-btn">{saving ? "Sending…" : "Send for review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
